// Stimulus controller for collection filter interactions.
// Intercepts form submission to build clean, SEO-friendly URLs with comma-separated params.
import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["filters", "products"]

    connect() {
        this.activePage = this.#currentPage()
        this.ignoreScrollUntil = 0
        this.scrollFrame = null
        this.restoreTimer = null

        this.handleScroll = this.#handleScroll.bind(this)
        this.handleMutations = this.#handleMutations.bind(this)
        this.handlePopState = this.#handlePopState.bind(this)
        this.mutationObserver = new MutationObserver(this.handleMutations)
        this.mutationObserver.observe(this.element, {childList: true, subtree: true})

        window.addEventListener("scroll", this.handleScroll, {passive: true})
        window.addEventListener("resize", this.handleScroll, {passive: true})
        window.addEventListener("popstate", this.handlePopState)
        this.#syncPageMarkers()
    }

    disconnect() {
        this.mutationObserver?.disconnect()
        window.removeEventListener("scroll", this.handleScroll)
        window.removeEventListener("resize", this.handleScroll)
        window.removeEventListener("popstate", this.handlePopState)

        if (this.scrollFrame) {
            cancelAnimationFrame(this.scrollFrame)
        }

        if (this.restoreTimer) {
            clearTimeout(this.restoreTimer)
        }
    }

    // Triggered when any filter checkbox or sort select changes
    filterChange(event) {
        this.#submitFilters()
    }

    // Triggered when mobile sort select changes
    mobileSortChange(event) {
        this.#submitFilters()
    }

    // Triggered when a selected filter chip is clicked to remove it
    removeFilter(event) {
        event.preventDefault()
        const tag = event.currentTarget.dataset.tag
        if (!tag) return

        // Uncheck in both desktop form and mobile offcanvas (prefix m_)
        const desktop = document.getElementById(tag)
        const mobile = document.getElementById(`m_${tag}`)
        if (desktop) desktop.checked = false
        if (mobile) mobile.checked = false

        this.#submitFilters()
    }

    // Builds clean comma-separated URL params and updates the collection_list turbo frame.
    // Produces: ?tags=celtic,braided&metals=14K&sort=price_asc
    // Instead of: ?tags%5B%5D=113&tags%5B%5D=104&metals%5B%5D=14K&sort_order=best_sellers
    // Uses manual string building (not URLSearchParams) to avoid %2C encoding of commas.
    // Collects from either the desktop form or the mobile offcanvas panel.
    #submitFilters() {
        const form = document.getElementById('collection-render-frm')
        const offcanvas = document.getElementById('filterOffcanvas')
        const desktop = form ? form.querySelector('.collection-filters-desktop') : null

        // Use visible filter UI only (desktop is in the DOM but hidden below lg).
        const desktopVisible = desktop && desktop.offsetParent !== null
        const source = desktopVisible ? desktop : offcanvas
        if (!source) return

        const parts = []

        const filterGroups = { 'tags[]': 'tags', 'p_tags[]': 'p_tags', 'metals[]': 'metals' }
        for (const [inputName, paramName] of Object.entries(filterGroups)) {
            const checked = source.querySelectorAll(`input[name="${inputName}"]:checked`)
            if (checked.length > 0) {
                const values = Array.from(checked).map(cb => cb.value).join(',')
                parts.push(`${paramName}=${values}`)
            }
        }

        // Sort: mobile offcanvas uses mobile_sort_order, desktop bar uses sort_order
        const sortName = desktopVisible ? 'sort_order' : 'mobile_sort_order'
        const sortSelect = source.querySelector(`select[name="${sortName}"]`)
        if (sortSelect && sortSelect.value && sortSelect.value !== 'best_sellers') {
            const sortMap = { 'best_sellers': 'best', 'price_asc': 'price_asc', 'price_desc': 'price_desc' }
            parts.push(`sort=${sortMap[sortSelect.value] || sortSelect.value}`)
        }

        const baseUrl = window.location.pathname
        const url = parts.length > 0 ? `${baseUrl}?${parts.join('&')}` : baseUrl

        this.#closeMobileFilterPanel()

        Turbo.visit(url, { frame: "collection_list", action: "advance" })
    }

    #closeMobileFilterPanel() {
        const offcanvasEl = document.getElementById("filterOffcanvas")
        if (!offcanvasEl) return

        const instance = window.bootstrap?.Offcanvas?.getInstance(offcanvasEl)
        if (instance) instance.hide()
    }

    #handleMutations() {
        this.#syncPageMarkers()
    }

    #syncPageMarkers() {
        this.pageMarkers = Array.from(
            this.element.querySelectorAll("[data-collection-page-url]")
        )
        this.#schedulePageUrlUpdate()
    }

    #handleScroll() {
        this.#schedulePageUrlUpdate()
    }

    #handlePopState() {
        this.activePage = this.#currentPage()
        this.ignoreScrollUntil = Date.now() + 500

        if (this.restoreTimer) {
            clearTimeout(this.restoreTimer)
        }

        this.restoreTimer = setTimeout(() => {
            this.ignoreScrollUntil = 0
            this.#schedulePageUrlUpdate()
        }, 500)
    }

    #schedulePageUrlUpdate() {
        if (this.scrollFrame) return

        this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null
            this.#updatePageUrl()
        })
    }

    #updatePageUrl() {
        if (!this.pageMarkers?.length || Date.now() < this.ignoreScrollUntil) return

        const activationLine = window.innerHeight * 0.35
        let activeMarker = this.pageMarkers[0]

        for (const marker of this.pageMarkers) {
            if (marker.getBoundingClientRect().top <= activationLine) {
                activeMarker = marker
            } else {
                break
            }
        }

        const url = activeMarker.dataset.collectionPageUrl
        const page = Number(activeMarker.dataset.collectionPage)
        if (!url || page === this.activePage) return

        const state = {
            ...(window.history.state || {}),
            collectionPage: page
        }

        window.history.pushState(state, "", url)
        this.activePage = page
    }

    #currentPage() {
        const page = Number(new URLSearchParams(window.location.search).get("page"))
        return page > 1 ? page : 1
    }
}
